import { API_BASE_URL, handleResponse, getAuthHeaders } from '@/lib/api-client';

export interface StudentProfile {
  id: string;
  user_id: string;
  username?: string;
  first_name: string;
  last_name: string;
  display_name: string;
  grade_level: number | null;
  fluency_enabled?: boolean;
  music_enabled?: boolean;
  created_at: string;
  updated_at: string;
}

export interface StudentSettingsUpdate {
  fluency_enabled?: boolean;
  music_enabled?: boolean;
}

export interface StudentProfileCreate {
  first_name: string;
  last_name: string;
  display_name: string;
  grade_level: number | null;
  username?: string;
  password?: string;
}

export interface ChildProgress {
  student_id: string;
  display_name: string;
  grade_level: number | null;
  total_sessions: number;
  total_stories_read: number;
  average_score: number | null;
  recent_activity: string | null;
}

export interface ChildActivityItem {
  item_id: string;
  type: 'reading' | 'flashcard' | 'fluency';
  title: string;
  subtitle: string | null;
  session_count: number;
  best_score: number;
  total_questions: number;
  best_wpm?: number | null;
  best_accuracy_pct?: number | null;
  last_activity_at: string;
  last_completed_at: string | null;
  total_duration_seconds: number | null;
}

export interface ActivitySessionDetail {
  session_id: string;
  score: number;
  total_questions: number;
  accuracy: number | null;
  duration_seconds: number | null;
  completed_at: string | null;
  started_at: string;
}

export interface ActivityDetailResponse {
  item_id: string;
  type: 'reading' | 'flashcard';
  title: string;
  subtitle: string | null;
  sessions: ActivitySessionDetail[];
}

export interface ChildDeckItem {
  id: string;
  title: string;
  description: string | null;
  deck_type: string;
  card_count: number;
  story_id: string | null;
  story_title: string | null;
  created_at: string;
  updated_at: string;
}

export interface InviteCreate {
  student_username?: string;
}

export interface Invite {
  id: string;
  invite_code: string;
  student_username: string | null;
  is_used: boolean;
  expires_at: string;
  created_at: string;
}

export interface AcceptInviteRequest {
  invite_code: string;
}

// NEW: /api/me/* endpoints - My resources based on role context
export const meApi = {
  // My Students (context-aware: parents get children, teachers get students, etc.)
  async getMyStudents(): Promise<StudentProfile[]> {
    const response = await fetch(`${API_BASE_URL}/me/students`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<StudentProfile[]>(response, 'Failed to fetch students');
  },

  async addStudent(data: StudentProfileCreate): Promise<StudentProfile> {
    const response = await fetch(`${API_BASE_URL}/me/students`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<StudentProfile>(response, 'Failed to add student');
  },

  async getStudent(studentId: string): Promise<StudentProfile> {
    const response = await fetch(`${API_BASE_URL}/me/students/${studentId}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<StudentProfile>(response, 'Failed to fetch student');
  },

  async updateStudent(studentId: string, data: StudentProfileCreate): Promise<StudentProfile> {
    const response = await fetch(`${API_BASE_URL}/me/students/${studentId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<StudentProfile>(response, 'Failed to update student');
  },

  async deleteStudent(studentId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/me/students/${studentId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse<void>(response, 'Failed to delete student');
  },

  // My Invites
  async createInvite(data: InviteCreate = {}): Promise<Invite> {
    const response = await fetch(`${API_BASE_URL}/me/invites`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<Invite>(response, 'Failed to create invite');
  },

  async getInvites(): Promise<Invite[]> {
    const response = await fetch(`${API_BASE_URL}/me/invites`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<Invite[]>(response, 'Failed to fetch invites');
  },
};

// Student resource endpoints with access control
export const studentsApi = {
  async getStudentProgress(studentId: string): Promise<ChildProgress> {
    const response = await fetch(`${API_BASE_URL}/students/${studentId}/progress`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<ChildProgress>(response, 'Failed to fetch student progress');
  },

  async getStudentActivity(studentId: string, limit: number = 20): Promise<ChildActivityItem[]> {
    const response = await fetch(
      `${API_BASE_URL}/students/${studentId}/activity?limit=${limit}`,
      {
        headers: getAuthHeaders(),
      }
    );
    return handleResponse<ChildActivityItem[]>(response, 'Failed to fetch student activity');
  },

  async getActivityDetail(studentId: string, itemType: string, itemId: string): Promise<ActivityDetailResponse> {
    const response = await fetch(
      `${API_BASE_URL}/students/${studentId}/activity/${itemType}/${itemId}`,
      {
        headers: getAuthHeaders(),
      }
    );
    return handleResponse<ActivityDetailResponse>(response, 'Failed to fetch activity detail');
  },

  async getStudentFlashcardDecks(
    studentId: string,
    params?: { deck_type?: string; source?: string },
  ): Promise<ChildDeckItem[]> {
    const queryParams = new URLSearchParams();
    if (params?.deck_type) queryParams.append('deck_type', params.deck_type);
    if (params?.source) queryParams.append('source', params.source);
    const qs = queryParams.toString();
    const response = await fetch(
      `${API_BASE_URL}/students/${studentId}/flashcard-decks${qs ? `?${qs}` : ''}`,
      { headers: getAuthHeaders() },
    );
    return handleResponse<ChildDeckItem[]>(response, 'Failed to fetch flashcard decks');
  },

  async updateStudentFlashcardDeck(
    studentId: string,
    deckId: string,
    data: { title?: string; description?: string },
  ): Promise<ChildDeckItem> {
    const response = await fetch(
      `${API_BASE_URL}/students/${studentId}/flashcard-decks/${deckId}`,
      {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      },
    );
    return handleResponse<ChildDeckItem>(response, 'Failed to update deck');
  },

  async updateStudentSettings(
    studentId: string,
    data: StudentSettingsUpdate,
  ): Promise<StudentProfile> {
    const response = await fetch(
      `${API_BASE_URL}/students/${studentId}/settings`,
      {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      },
    );
    return handleResponse<StudentProfile>(response, 'Failed to update student settings');
  },
};

// Invite actions (public)
export const invitesApi = {
  async acceptInvite(inviteCode: string): Promise<{ message: string; parent_username: string; student_id: string }> {
    const response = await fetch(`${API_BASE_URL}/invites/accept`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ invite_code: inviteCode }),
    });
    return handleResponse(response, 'Failed to accept invite');
  },
};

// DEPRECATED: Use meApi and studentsApi instead
export const legacyParentApi = {
  // Children management - DEPRECATED: Use meApi.getMyStudents() instead
  async getChildren(): Promise<StudentProfile[]> {
    const response = await fetch(`${API_BASE_URL}/parents/children`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<StudentProfile[]>(response, 'Failed to fetch children');
  },

  async addChild(data: StudentProfileCreate): Promise<StudentProfile> {
    const response = await fetch(`${API_BASE_URL}/parents/children`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<StudentProfile>(response, 'Failed to add child');
  },

  async getChild(studentId: string): Promise<StudentProfile> {
    const response = await fetch(`${API_BASE_URL}/parents/children/${studentId}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<StudentProfile>(response, 'Failed to fetch child');
  },

  async updateChild(studentId: string, data: StudentProfileCreate): Promise<StudentProfile> {
    const response = await fetch(`${API_BASE_URL}/parents/children/${studentId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<StudentProfile>(response, 'Failed to update child');
  },

  async deleteChild(studentId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/parents/children/${studentId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse<void>(response, 'Failed to delete child');
  },

  async getChildProgress(studentId: string): Promise<ChildProgress> {
    const response = await fetch(`${API_BASE_URL}/parents/children/${studentId}/progress`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<ChildProgress>(response, 'Failed to fetch child progress');
  },

  async getChildSessions(studentId: string, limit: number = 20): Promise<ChildActivityItem[]> {
    const response = await fetch(
      `${API_BASE_URL}/parents/children/${studentId}/sessions?limit=${limit}`,
      {
        headers: getAuthHeaders(),
      }
    );
    return handleResponse<ChildActivityItem[]>(response, 'Failed to fetch child sessions');
  },

  // Invites - DEPRECATED: Use meApi.createInvite/getInvites and invitesApi.acceptInvite
  async createInvite(data: InviteCreate = {}): Promise<Invite> {
    const response = await fetch(`${API_BASE_URL}/parents/invites`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<Invite>(response, 'Failed to create invite');
  },

  async getInvites(): Promise<Invite[]> {
    const response = await fetch(`${API_BASE_URL}/parents/invites`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<Invite[]>(response, 'Failed to fetch invites');
  },

  async acceptInvite(inviteCode: string): Promise<{ message: string; parent_username: string; student_id: string }> {
    const response = await fetch(`${API_BASE_URL}/parents/invites/accept`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ invite_code: inviteCode }),
    });
    return handleResponse(response, 'Failed to accept invite');
  },
};
